import { Flex, Text, Tooltip } from '@mantine/core';
import { IconFileText, IconTargetArrow } from '@tabler/icons-react';
import cn from 'classnames';
import * as React from 'react';

import ScoreBadge from '@/components/shared/ScoreBadge';
import { DocumentSearchItem } from '@/entities/DocumentsSearch';

import DocumentAvailabilityIcon from '../../DocumentAvailabilityIcon';
import { getSimilarityColor, normalizeScoreToPercent } from '../../ScoreBadge/utils';

import Quote from './Quote';

import s from './SearchTreeItem.module.scss';

type Props = {
  item: DocumentSearchItem;
  actions: React.ReactNode;
  onPathClick?: (e: React.MouseEvent) => void;
};

const SearchTreeItem: React.FC<Props> = ({ item, actions, onPathClick }) => {
  const isLocked = !item.contentAvailable;
  const relevancePercent = normalizeScoreToPercent(item.combinedScore);
  const hasRelevance = !isLocked && relevancePercent !== null;

  const mainIcon = isLocked ? (
    <DocumentAvailabilityIcon reason={item.availabilityIconReason} />
  ) : hasRelevance ? (
    <Tooltip label={`Релевантность: ${relevancePercent}%`} position="top" openDelay={300} withArrow>
      <span className={s['root__relevance-icon']} role="img" aria-label={`Релевантность ${relevancePercent} процентов`}>
        <IconTargetArrow size={16} color={getSimilarityColor(relevancePercent)} stroke={2} />
      </span>
    </Tooltip>
  ) : (
    <IconFileText className={s.root__icon} color={item.iconColor} size={16} />
  );

  const titleRow = (
    <Flex className={s.root__title} align="center" gap="xs">
      <Flex align="center" gap={4} className={s['root__file-icons']}>
        {mainIcon}
      </Flex>
      <Text size="sm" lineClamp={1} className={cn(isLocked && s.root__name_muted)}>
        {item.name}
      </Text>
    </Flex>
  );

  return (
    <>
      <Flex align="center" justify="space-between">
        {titleRow}
        <Flex align="center" gap="xxs">
          {hasRelevance && <ScoreBadge label="Релевантность" value={item.combinedScore} size="sm" />}
          {actions}
        </Flex>
      </Flex>
      <Flex direction="column" className={s.root__offset}>
        {onPathClick ? (
          <Text component="button" size="xxs" c="dimmed" lh="xxs" className={s.root__path} onClick={onPathClick}>
            {item.pathString}
          </Text>
        ) : (
          <Text size="xxs" c="dimmed" lh="xxs">
            {item.pathString}
          </Text>
        )}
        <Text size="xxs" c="dimmed" lh="xxs">
          Страница: {item.pageNumber}
        </Text>
        <Quote item={item} />
      </Flex>
    </>
  );
};

export default React.memo(SearchTreeItem);
